import ErrorBanner from "components/ui/ErrorBanner";
import classNames from "classnames";
import {
  DICTIONARY_MODES,
  DictionaryMode,
  DictionaryToolsContext,
  DictionaryToolsContextValue,
} from "contexts/DictionaryToolsContext";
import { ErrorBoundary } from "components/ErrorBoundary";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { CSSProperties, PropsWithChildren, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "contexts/UserContext";
import { useRouteMeta } from "utils/routing";
import Tabs from "components/ui/Tabs";
import SearchInputWithSuggestions from "./SearchInputWithSuggestions/SearchInputWithSuggestions";
import DefineTab from "./DefineTab";
import SaveTab from "./SaveTab";
import TranslateTab from "./TranslateTab";
import { useSidebar } from "./useSidebar";
import { useSelectionMenu } from "./useSelectionMenu";
import { useDictionarySearch } from "./useDictionarySearch";
import "./tabs.css";
import "./DictionaryTools.css";
import type { TranslationKey } from "services/i18n/keys";

const LOOKUP_FAILED_TEXT = "Dictionary.panel.lookupFailed";

const MODE_LABEL_KEYS: Record<DictionaryMode, TranslationKey> = {
  define: "Dictionary.panel.modeDefine",
  translate: "Dictionary.panel.modeTranslate",
  save: "Dictionary.panel.modeMyVocab",
};

export default function DictionaryTools(props: PropsWithChildren) {
  const { t } = useTranslation();
  const user = useUser(false);
  const enabled = useRouteMeta().tools && user?.canUseDictionaryTools === true;

  const {
    isOpen: isSidebarOpen,
    width: sidebarWidth,
    isResizing,
    drawerRef,
    splitterProps,
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleSidebar,
  } = useSidebar();

  const {
    owner: selectionOwner,
    update: updateSelectionState,
    reset: resetSelection,
  } = useSelectionMenu();

  const {
    text,
    setText,
    mode,
    onModeChanged,
    onSearch,
    applyLookup,
    myWordsSearchText,
    clearMyWordsFilter,
    result,
    isError,
    isFetching,
  } = useDictionarySearch();

  const openSidebar = useCallback(
    (openMode: DictionaryMode, selectionText: string) => {
      if (!selectionText) {
        return;
      }
      openDrawer();
      applyLookup(openMode, selectionText);
    },
    [openDrawer, applyLookup]
  );

  const closeSidebar = useCallback(() => {
    closeDrawer();
    resetSelection();
  }, [closeDrawer, resetSelection]);

  const onClose = () => {
    clearMyWordsFilter();
    closeSidebar();
  };

  const contextValue = useMemo<DictionaryToolsContextValue>(
    () => ({
      enabled,
      isSidebarOpen,
      openSidebar,
      toggleSidebar,
      closeSidebar,
      selectionMenuOwner: selectionOwner,
      updateSelectionState,
    }),
    [
      enabled,
      isSidebarOpen,
      openSidebar,
      toggleSidebar,
      closeSidebar,
      selectionOwner,
      updateSelectionState,
    ]
  );

  const defineContent = isError ? t(LOOKUP_FAILED_TEXT) : result;
  const isSelectionOpened = selectionOwner === "sidebar";

  let activeTab = null;
  switch (mode) {
    case "define":
      activeTab = (
        <DefineTab defineContent={defineContent} isSelectionOpened={isSelectionOpened} />
      );
      break;
    case "translate":
      activeTab = (
        <TranslateTab searchText={text} isSelectionOpened={isSelectionOpened} />
      );
      break;
    case "save":
      activeTab = (
        <SaveTab
          myWordsSearchText={myWordsSearchText}
          onModeChanged={onModeChanged}
          onTextChanged={setText}
        />
      );
      break;
    default:
  }

  const drawerStyle = {
    "--sidebar-width": `${sidebarWidth}px`,
  } as CSSProperties;

  const sidebarPanel = (
    <div className="sidebar-wrapper">
      <div className="splitter" {...splitterProps} />
      <div className="dictionary-tools-panel">
        <div className="dictionary-header">
          <h2>{t("Dictionary.panel.title")}</h2>
          <button
            type="button"
            className="close-action"
            onClick={onClose}
            aria-label={t("Common.actions.close")}
          >
            ✕
          </button>
        </div>

        <div className="dictionary-content">
          <SearchInputWithSuggestions
            value={text}
            onValueChanged={setText}
            onSearch={onSearch}
          />
          <div className="dictionary-tools-result-panel">
            <Tabs
              className="dictionary-mode-tabs"
              aria-label={t("Dictionary.panel.title")}
              items={DICTIONARY_MODES.map(m => ({
                value: m,
                label: t(MODE_LABEL_KEYS[m]),
              }))}
              value={mode}
              onChange={value => onModeChanged(value as DictionaryMode)}
            />
            {isFetching && (
              <div className="spinner-container">
                <TalaeraSpinner />
              </div>
            )}
            {!isFetching && activeTab}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DictionaryToolsContext.Provider value={contextValue}>
      {enabled ? (
        <div className="page-content-with-sidebar">
          <div
            className={classNames("drawer-root", {
              open: isSidebarOpen,
              resizing: isResizing,
            })}
            style={drawerStyle}
            ref={drawerRef}
            role="navigation"
          >
            <ErrorBoundary
              fallback={<ErrorBanner>{t("Dictionary.panel.genericError")}</ErrorBanner>}
            >
              <div id="sidebar-id">{sidebarPanel}</div>
            </ErrorBoundary>
            <div id="sidebar-content-id">{props.children}</div>
          </div>
        </div>
      ) : (
        props.children
      )}
    </DictionaryToolsContext.Provider>
  );
}
